/**
 * The events module's routes, mounted by the site at `/events` and `/events/*` (see README). Client-side
 * only, as the site is (ARCHITECTURE.md "Old URLs"):
 *   /events                 the landing; ?e={slug} the share view; ?notice=unavailable the notice
 *   /events/{slug}          the public, indexable event page (WHO-OWNS-WHICH-URL.md §3)
 *   /events/past            → the gate with /events/past
 *   /events/my, /events/my/* → the gate with that path
 *   /events/{slug}/rsvp?s=  → the gate with /events/rsvp?e={slug}&s={id}
 *   /events/go?to=          → the backend gate (only reached when no Cloudflare rule forwards it yet)
 * Anything else under /events → the event page's not-found view, which is noindex.
 */
import { useEffect } from "react";
import { Navigate, Route, Routes, useParams, useSearchParams } from "react-router-dom";
import EventsLanding from "./pages/EventsLanding";
import EventDetail from "./pages/EventDetail";
import { EVENT_SESSION_ID_RE, gateLink, isSlug, leaveTo, myTarget, rsvpTarget } from "./lib/gate";

export const BACKEND_GATE_PATH = "/api/events/go";

/** A full-page redirect to the gate (a plain link stays underneath for the half second it takes). */
export function GateRedirect({ target, href = gateLink(target) }) {
  useEffect(() => { leaveTo(href); }, [href]);
  return (
    <main className="ev-root" style={{ padding: "40px 20px" }}>
      <p>Taking you to our events site… <a href={href} data-testid="events-gate-continue">Continue</a></p>
    </main>
  );
}

function RsvpRedirect() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  if (!isSlug(slug)) return <Navigate to="/events" replace />;
  const s = params.get("s");
  return <GateRedirect target={s && EVENT_SESSION_ID_RE.test(s) ? rsvpTarget(slug, s) : `/events/${slug}`} />;
}

function MyRedirect() {
  const { "*": rest } = useParams();
  return <GateRedirect target={myTarget(rest)} />;
}

function GoPassThrough() {
  const [params] = useSearchParams();
  const to = params.get("to") || "/events";
  return <GateRedirect target={to} href={`${BACKEND_GATE_PATH}?to=${encodeURIComponent(to)}`} />;
}

export default function EventsRouter() {
  return (
    <Routes>
      <Route index element={<EventsLanding />} />
      <Route path="go" element={<GoPassThrough />} />
      <Route path="past" element={<GateRedirect target="/events/past" />} />
      <Route path="my" element={<GateRedirect target="/events/my" />} />
      <Route path="my/*" element={<MyRedirect />} />
      <Route path=":slug/rsvp" element={<RsvpRedirect />} />
      <Route path=":slug" element={<EventDetail />} />
      {/* An unknown path under /events renders the noindex not-found view rather than bouncing to the landing:
          the platform cannot answer 404 outside /api, and a soft-404 that looks like a real page invites Google
          to index junk URLs (ANSWERS-ON-STATUS-CODES-AND-CUTOVER.md §1). */}
      <Route path="*" element={<EventDetail />} />
    </Routes>
  );
}
